// Cache de sesión en memoria, con TTL y DUEÑO.
// Los valores viven sólo mientras la pestaña esté abierta (este `Map`, no
// localStorage/sessionStorage). El dueño —quién tiene la sesión— es lo único que
// este módulo sí escribe en `sessionStorage`, para poder limpiar al cambiar de
// cuenta los caches que otras pantallas guardan ahí (ver `CLAVES_DE_CUENTA_EN_SESION`).

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * De quién son los datos que hay guardados — ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas.
 *
 * Sin esto el cache cruzaba cuentas. Las claves son globales (`user:profile`, no
 * `user:profile:<id>`) y el logout de la app es `signOut()` + `navigate('/auth')`:
 * una navegación de cliente que NO recarga la página, así que este módulo —y su
 * `Map`— sobrevivían al cambio de cuenta. Quien cerraba sesión y entraba con otra
 * dentro de los 5 minutos de TTL recibía el perfil del anterior, `id_company`
 * incluido, porque `getCached` cortaba la query antes de llegar a la DB.
 *
 * `null` = todavía no se sabe de quién es la sesión, y entonces no hay nada que
 * servir: falla cerrado, yendo a la DB.
 */
let ownerId: string | null = null;

/**
 * Claves de `sessionStorage` con datos de UNA cuenta y nombre GLOBAL (sin el id del
 * usuario adentro) — ADR-2026-09-11-el-cache-de-sessionstorage-tambien-tiene-dueno.
 *
 * El gemelo en `sessionStorage` de `CLAVES_DE_CUENTA` (localStorage) de
 * `lib/cerrarSesion`, y el mismo criterio para entrar: "describe a una cuenta", no
 * "es un dato de UI". Lo que las hace peligrosas es que varias pantallas las leen en
 * el INITIALIZER de su `useState` —sincrónico, antes de resolver quién es el usuario,
 * justamente para pintar sin skeleton— así que lo cacheado se renderiza tal cual
 * hasta que vuelve el fetch de la cuenta nueva.
 *
 * Por qué la recarga dura de `cerrarSesion` no alcanza (ahí sí alcanza para el `Map`
 * de este módulo y para el resto del estado en memoria): `sessionStorage` SOBREVIVE a
 * recargar el documento — se borra recién al cerrar la pestaña. Quien cerraba sesión y
 * entraba con otra cuenta en la misma pestaña veía, en el primer paint, las plantillas
 * de la empresa anterior (las de catálogo traen las PROPIAS de la cuenta además de las
 * globales), su catálogo de productos y su borrador a medio configurar.
 *
 * Quedan AFUERA a propósito las preferencias del dispositivo y los caches de catálogos
 * globales, que son iguales para cualquier cuenta (`atlas-v2-home-poses-cache`).
 *
 * Si agregás una llave nueva que describa a la cuenta, va acá. El test de este módulo
 * chequea contra las constantes de los módulos que las definen.
 */
const CLAVES_DE_CUENTA_EN_SESION = [
  // Catálogo de productos de la empresa — `pages/v2/productsCache.ts`.
  'atlas-v2-user-products-cache',
  // Plantillas/colecciones: cada una trae las GLOBALES de Atlas + las privadas de la
  // empresa, así que son datos de la cuenta aunque parezcan un catálogo.
  // `InterfazV2`, `InterfazV2Plantillas`, `ColeccionDetalle`, y los dos de Belleza.
  'atlas-v2-home-collections-cache',
  'atlas-v2-plantillas-cache',
  'atlas-v2-collections-cache',
  'atlas-v2-plantillas-belleza-cache',
  'atlas-v2-collections-belleza-cache',
  // Rubro de la empresa, cacheado para el primer paint de la home.
  'atlas-v2-home-design-rubro',
  // Borrador de la última generación, con los ids de producto adentro —
  // `pages/v2/restoreV2.ts`.
  'atlas-v2-restore',
  // Filtros/estado del historial de imágenes de la cuenta.
  'atlas-history-state',
  // Hero del flujo de influencer y banderas de "ya generó" del alta / anuncios.
  'influencer-reverse-hero',
  'media_initial_generation_started',
  'atlas:crear-anuncio:has-generated',
];

/**
 * El chat del Empleado IA: mensajes, id del chat abierto y el flag de investigación.
 *
 * Van por SUFIJO y no por nombre completo porque la clave lleva adelante el prefijo de
 * quien monta el hook (`clavesDeStorage(prefijo)` en `pages/v2/useV2Chat.ts`), y las
 * pantallas eligen el suyo: enumerarlas una por una se desactualiza con la próxima.
 */
const SUFIJOS_DE_CUENTA_EN_SESION = [
  '-chat-messages',
  '-current-chat-id',
  '-research-enabled',
];

/**
 * El dueño, PERSISTIDO en `sessionStorage`.
 *
 * `ownerId` no sirve para decidir esto: es estado de módulo y arranca en `null` en cada
 * carga del documento, así que un F5 con la MISMA cuenta se vería igual que un cambio de
 * cuenta y borraría caches que el refresh justamente tiene que conservar (es su razón de
 * ser: pintar la home sin skeleton). El sello vive donde viven los caches que protege y
 * muere con ellos, al cerrar la pestaña.
 */
const CLAVE_DUENO_EN_SESION = 'atlas-cache-owner';

function duenoSellado(): string | null {
  try {
    return sessionStorage.getItem(CLAVE_DUENO_EN_SESION);
  } catch {
    return null; // storage bloqueado (Safari privado): no hay caches que proteger
  }
}

/**
 * Tira los caches de `sessionStorage` de la cuenta que se fue y sella la nueva.
 * Exportada para el test; en la app la llama sólo `setCacheOwner`.
 */
export function purgarCachesDeCuentaEnSesion(duenoNuevo: string | null): void {
  try {
    for (const clave of CLAVES_DE_CUENTA_EN_SESION) {
      sessionStorage.removeItem(clave);
    }
    for (const clave of Object.keys(sessionStorage)) {
      if (SUFIJOS_DE_CUENTA_EN_SESION.some((s) => clave.endsWith(s))) {
        sessionStorage.removeItem(clave);
      }
    }
    if (duenoNuevo) sessionStorage.setItem(CLAVE_DUENO_EN_SESION, duenoNuevo);
    else sessionStorage.removeItem(CLAVE_DUENO_EN_SESION);
  } catch {
    /* storage bloqueado o inexistente (tests sin DOM): no hay nada que limpiar */
  }
}

/**
 * Declara de quién es la sesión. Si cambió de dueño —logout, o el login de otra
 * cuenta— tira TODO lo guardado: el `Map` de acá y los caches que otras pantallas
 * dejaron en `sessionStorage` (ADR-2026-09-11-el-cache-de-sessionstorage-tambien-tiene-dueno).
 *
 * Lo llama `lib/cerrarSesion` y el `onAuthStateChange` de `App.tsx`, que es el
 * único listener que corre siempre montado.
 *
 * Los dos "cambió de dueño" se preguntan por separado a propósito: el del `Map` contra
 * `ownerId` (memoria, se reinicia con el documento) y el de `sessionStorage` contra el
 * sello (sobrevive al refresh). Mezclarlos rompe uno de los dos casos.
 */
export function setCacheOwner(userId: string | null): void {
  if (userId !== duenoSellado()) purgarCachesDeCuentaEnSesion(userId);
  if (userId === ownerId) return; // mismo usuario: el cache sigue siendo suyo
  ownerId = userId;
  store.clear();
}

/**
 * De quién es la sesión AHORA. Se toma ANTES de salir a buscar y se devuelve en
 * `setCached`, para los datos que no son de ninguna cuenta en particular (los
 * mensajes de inicio) o cuando el `user.id` no está a mano.
 *
 * Ojo: sirve tomado antes del `await`. Llamarlo en la misma línea del
 * `setCached` no protege de nada — siempre va a coincidir consigo mismo.
 */
export function cacheOwner(): string | null {
  return ownerId;
}

/**
 * Para los guards de flujos async atados a un usuario (el `checkUserProfile` de
 * App.tsx): la invocación VIEJA que despierta de un await y encuentra que la
 * sesión ya no es de su usuario, ¿debe apagar el spinner global?
 *
 * Solo si NO hay dueño nuevo (logout): ahí no corre ninguna invocación nueva y
 * el spinner quedaría tapando /auth para siempre. Con dueño nuevo el loading es
 * de SU invocación (el listener la disparó junto con el cambio de dueño):
 * apagarlo desde la vieja renderiza la ruta protegida antes de que el nuevo
 * termine de decidir gate de celular / empresa / wizard (review 2026-09-03).
 */
export function debeApagarLoadingTrasMismatch(duenoActual: string | null): boolean {
  return duenoActual === null;
}

export function getCached<T>(key: string): T | null {
  const entry = store.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return null;
  }
  return entry.value as T;
}

/**
 * Guarda un valor declarando A QUÉ SESIÓN pertenece. Si para cuando llega ya no
 * es la sesión vigente, el valor se DESCARTA.
 *
 * `owner` es obligatorio a propósito — ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas-2.
 * Vaciar el cache en `setCacheOwner` no alcanza: entre que un consumidor
 * dispara su query y que le contestan hay un `await`, y en ese hueco la sesión
 * puede cambiar de dueño SIN que este tab haga logout (otra pestaña, o un login
 * sobre una sesión viva — los dos casos que el listener de `App.tsx` dice cubrir).
 * La respuesta vieja llegaba después del `clear()` y volvía a llenar el `Map`
 * con datos de la cuenta anterior; como no volvía a haber cambio de dueño, nadie
 * lo limpiaba y la cuenta nueva leía ese `id_company`, plan y rubro hasta que
 * venciera el TTL.
 *
 * El dueño NO se puede resolver acá adentro: en el momento del `set` el dato ya
 * viajó, y estamparlo con el dueño ACTUAL lo firmaría justamente con la cuenta
 * equivocada. La identidad tiene que cruzar el `await` con el llamador; que sea
 * un parámetro obligatorio es lo que evita que alguien se olvide.
 *
 * Con esta guarda el `Map` sólo contiene lo escrito bajo el dueño vigente, así
 * que `getCached` no necesita chequear nada.
 */
export function setCached<T>(
  key: string,
  value: T,
  owner: string | null,
  ttlMs = DEFAULT_TTL_MS,
): void {
  if (owner !== ownerId) return; // llegó tarde: es de una sesión que ya no corre
  store.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function invalidateCache(key: string): void {
  store.delete(key);
}

export function invalidateCacheByPrefix(prefix: string): void {
  for (const k of store.keys()) {
    if (k.startsWith(prefix)) store.delete(k);
  }
}

// Pre-defined cache keys
export const CACHE_KEYS = {
  USER_PROFILE: 'user:profile',
  COMPANY_DATA: 'company:data',
  COMPANY_CONFIG: 'company:config',
  MENSAJES_INICIO: 'mensajes:inicio',
  TN_STATUS: 'company:tn_status',
} as const;
